// ---------------------------------------------------------------------------
// Dashboard derivations — pure functions that turn a StartMyDayResponse into
// the four things the new briefing dashboard leads with:
//   1. One next action
//   2. Three priorities
//   3. A risk strip (at-risk / breach only)
//   4. A one-line capacity strip
//
// Everything here is pure TypeScript, zero React, unit-testable. Screens
// should import these and memoise the results, not re-derive in render.
// ---------------------------------------------------------------------------

import type {
  CapacitySummary,
  Priority,
  StartMyDayResponse,
  WorkItem,
  ScheduledWorkBlock,
  CalendarEvent,
} from '../types/models';

// Numeric rank for the Priority enum — higher = more important. Used
// wherever we sort work items.
const PRIORITY_RANK: Record<Priority, number> = {
  Critical: 4,
  High: 3,
  Medium: 2,
  Low: 1,
};

export function comparePriority(a: Priority, b: Priority): number {
  return PRIORITY_RANK[b] - PRIORITY_RANK[a];
}

// ---------------------------------------------------------------------------
// Next action
// ---------------------------------------------------------------------------

export interface NextAction {
  /** Verb-first copy, e.g. "Start focus block". */
  verb: string;
  /** The thing to do, e.g. "Draft Q1 board paper". */
  label: string;
  /** HH:mm local, if the action is time-bound. */
  startTime?: string;
  /** Duration hint in minutes. */
  durationMinutes?: number;
  /** Work item the action is attached to, if any — lets the card link. */
  workItemId?: string;
  /** Route the user should land on when the card's primary button fires. */
  href?: string;
  /** Subtle label under the action, e.g. the driver title for context. */
  context?: string;
}

function hhmm(iso: string): string {
  // The API returns ISO timestamps. Locale-neutral HH:mm display.
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function minutesBetween(aIso: string, bIso: string): number {
  return Math.round((new Date(bIso).getTime() - new Date(aIso).getTime()) / 60000);
}

export function deriveNextAction(
  data: StartMyDayResponse | null,
  now: Date = new Date(),
): NextAction | null {
  if (!data) return null;

  // Prefer the first scheduled block that has not yet ended. That is the
  // committed, time-bound thing the user should be doing next.
  const futureBlocks = data.scheduledBlocks
    .filter((b) => new Date(b.endTime).getTime() > now.getTime())
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const nextBlock = futureBlocks[0];
  if (nextBlock && nextBlock.workItem) {
    const wi = nextBlock.workItem;
    const duration = minutesBetween(nextBlock.startTime, nextBlock.endTime);
    return {
      verb: actionVerbForWorkItem(wi),
      label: wi.title,
      startTime: hhmm(nextBlock.startTime),
      durationMinutes: duration,
      workItemId: wi.id,
      href: `/work-items?id=${wi.id}`,
      context: wi.strategicDriver?.title,
    };
  }

  // No scheduled block left today: surface the next real meeting within the
  // next 90 minutes, since "prep for meeting" is a common executive next-move.
  const upcomingMeetings = data.calendarEvents
    .filter((e) => {
      const start = new Date(e.startTime).getTime();
      return start > now.getTime() && start - now.getTime() < 90 * 60 * 1000;
    })
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  if (upcomingMeetings[0]) {
    const m = upcomingMeetings[0];
    return {
      verb: 'Prepare for',
      label: m.title,
      startTime: hhmm(m.startTime),
      durationMinutes: minutesBetween(m.startTime, m.endTime),
    };
  }

  // Still nothing: fall back to the highest-priority overflow item as the
  // most useful thing the user could do with unstructured time.
  const topOverflow = [...data.overflowItems]
    .sort((a, b) => comparePriority(a.priority, b.priority))[0];

  if (topOverflow) {
    return {
      verb: actionVerbForWorkItem(topOverflow),
      label: topOverflow.title,
      durationMinutes: topOverflow.estimatedMinutes || undefined,
      workItemId: topOverflow.id,
      href: `/work-items?id=${topOverflow.id}`,
      context: topOverflow.strategicDriver?.title,
    };
  }

  // Nothing scheduled, no meetings, no overflow. The right thing is to
  // close the day and plan tomorrow.
  return {
    verb: 'Plan tomorrow',
    label: 'Set up tomorrow\u2019s day',
    href: '/',
  };
}

function actionVerbForWorkItem(wi: WorkItem): string {
  switch (wi.type) {
    case 'Decision':
      return 'Decide on';
    case 'Briefing':
      return 'Read briefing';
    case 'Review':
      return 'Review';
    case 'FollowUp':
      return 'Follow up on';
    case 'Draft':
      return 'Draft';
    case 'Reading':
      return 'Read';
    case 'Task':
    default:
      return 'Start';
  }
}

// ---------------------------------------------------------------------------
// Priorities — top N items for today, driver-tagged.
// ---------------------------------------------------------------------------

export interface PriorityItem {
  workItem: WorkItem;
  /** HH:mm if the item has a scheduled block today. */
  scheduledTime?: string;
  /** Minutes planned for it today. */
  plannedMinutes?: number;
}

export function derivePriorities(
  data: StartMyDayResponse | null,
  max: number = 3,
): PriorityItem[] {
  if (!data) return [];

  // Build one list of candidate items with their scheduled-block context.
  // Scheduled items are the source of truth for "what Cadence placed on the
  // day"; overflow items fill the list if fewer than `max` are scheduled.
  const byId = new Map<string, PriorityItem>();

  for (const block of data.scheduledBlocks) {
    if (!block.workItem) continue;
    // Skip completed items — they should not be a "priority today".
    if (block.workItem.status === 'Done' || block.workItem.status === 'Cancelled') continue;
    const existing = byId.get(block.workItem.id);
    const minutes = minutesBetween(block.startTime, block.endTime);
    if (!existing) {
      byId.set(block.workItem.id, {
        workItem: block.workItem,
        scheduledTime: hhmm(block.startTime),
        plannedMinutes: minutes,
      });
    } else if (existing.plannedMinutes !== undefined) {
      existing.plannedMinutes += minutes;
    }
  }

  for (const wi of data.overflowItems) {
    if (wi.status === 'Done' || wi.status === 'Cancelled') continue;
    if (byId.has(wi.id)) continue;
    byId.set(wi.id, { workItem: wi });
  }

  // Sort by priority, then by scheduled-time (earlier first).
  // Using Array.from rather than iterator spread to stay compatible with the
  // ES5 target set in tsconfig.
  return Array.from(byId.values())
    .sort((a, b) => {
      const p = comparePriority(a.workItem.priority, b.workItem.priority);
      if (p !== 0) return p;
      if (a.scheduledTime && b.scheduledTime) {
        return a.scheduledTime.localeCompare(b.scheduledTime);
      }
      if (a.scheduledTime) return -1;
      if (b.scheduledTime) return 1;
      return 0;
    })
    .slice(0, max);
}

// ---------------------------------------------------------------------------
// Risks — structured, derived from the existing DailyPlan + Capacity fields
// plus overflow/carry-forward counts. No backend changes needed.
//
// Only at-risk and breach are surfaced. Absence of items is the good state,
// per the UX recommendation §5.1.
// ---------------------------------------------------------------------------

export type RiskSeverity = 'atRisk' | 'breach';

export type RiskType =
  | 'capacity-overload'
  | 'overflow'
  | 'carry-forward'
  | 'focus-deficit'
  | 'decision-backlog'
  | 'warning';

export interface StructuredRisk {
  severity: RiskSeverity;
  type: RiskType;
  label: string;
  detail?: string;
}

export interface RiskDerivationInputs {
  pendingDecisionCount?: number;
}

export function deriveRisks(
  data: StartMyDayResponse | null,
  inputs: RiskDerivationInputs = {},
): StructuredRisk[] {
  if (!data) return [];
  const risks: StructuredRisk[] = [];

  // Capacity overload: the most important signal the UI can show.
  if (data.capacity.isOverloaded) {
    const overBy = Math.max(
      0,
      data.capacity.meetingMinutes + data.capacity.plannedMinutes - data.capacity.availableMinutes,
    );
    risks.push({
      severity: 'breach',
      type: 'capacity-overload',
      label: 'Day is overloaded',
      detail: overBy > 0 ? `${overBy} minutes over available time` : undefined,
    });
  }

  // Overflow: items the scheduler could not place.
  if (data.overflowItems.length > 0) {
    risks.push({
      severity: data.overflowItems.length >= 3 ? 'breach' : 'atRisk',
      type: 'overflow',
      label: `${data.overflowItems.length} item${data.overflowItems.length === 1 ? '' : 's'} will not fit today`,
    });
  }

  // Carry-forward: items pushed from earlier days.
  if (data.carryForwardItems.length >= 3) {
    risks.push({
      severity: data.carryForwardItems.length >= 5 ? 'breach' : 'atRisk',
      type: 'carry-forward',
      label: `${data.carryForwardItems.length} items carried forward`,
      detail: 'Things keep getting pushed. Review what to drop.',
    });
  }

  // Focus deficit: fewer than 60 minutes of protected time after meetings.
  const residual =
    data.capacity.availableMinutes - data.capacity.meetingMinutes - data.capacity.plannedMinutes;
  if (residual > 0 && residual < 60) {
    risks.push({
      severity: 'atRisk',
      type: 'focus-deficit',
      label: 'Less than an hour of unplanned time',
      detail: 'Meetings and planned work fill the day. No margin for the unexpected.',
    });
  }

  // Decision backlog, if the caller knows how many decisions are pending.
  if (inputs.pendingDecisionCount !== undefined && inputs.pendingDecisionCount >= 3) {
    risks.push({
      severity: inputs.pendingDecisionCount >= 5 ? 'breach' : 'atRisk',
      type: 'decision-backlog',
      label: `${inputs.pendingDecisionCount} decisions waiting`,
    });
  }

  // Free-form warnings from the backend — surface as at-risk items.
  for (const w of data.warnings) {
    risks.push({ severity: 'atRisk', type: 'warning', label: w });
  }

  return risks;
}

// ---------------------------------------------------------------------------
// Capacity — one-line summary of committed / protected / free minutes.
// ---------------------------------------------------------------------------

export interface CapacityLine {
  committedMinutes: number;
  protectedMinutes: number;
  slackMinutes: number;
  /** True when committed + protected exceeds available; caller should show
   *  the risk strip rather than a negative free figure. */
  overloaded: boolean;
}

export function deriveCapacity(capacity: CapacitySummary): CapacityLine {
  const committed = capacity.meetingMinutes;
  const protectedMin = capacity.plannedMinutes;
  const slack = capacity.availableMinutes - committed - protectedMin;
  return {
    committedMinutes: Math.max(0, committed),
    protectedMinutes: Math.max(0, protectedMin),
    slackMinutes: Math.max(0, slack),
    overloaded: capacity.isOverloaded || slack < 0,
  };
}

// Convenience formatter used by CapacityStrip and NextActionCard.
export function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// Re-export a few type aliases for internal use; keeps imports short.
export type { ScheduledWorkBlock, CalendarEvent };
